import { isEmpty, toString } from 'lodash';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SidebarAccordion } from 'src/components/accordion';
import { Drawer } from 'src/components/drawer';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import { InputWidget } from 'src/components/widget';
import { PATHS } from 'src/constants/paths';
import { useDeleteVariation, useModal } from 'src/hooks';
import {
  type IAssemblyConfigLayout,
  IAssembly,
  IAssemblyComponent,
  IStatus,
} from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { SectionDetailsSidebar } from '../sectionDetailsSidebar';
import { AssemblyEditPageSidebarProps } from './AssemblyEditPageSidebar.interface';
import {
  Container,
  InputContainer,
  RemoveVariationButton,
  RemoveVariationButtonContainer,
  StartIcon,
  TagLabel,
} from './AssemblyEditPageSidebar.styled';
import { SectionRotationDynamicSections } from './SectionRotationDynamicSections';

export const AssemblyEditPageSidebar = (
  props: AssemblyEditPageSidebarProps,
): JSX.Element => {
  const { control, formState, getValues, setValue, watch } = useFormContext<
    Partial<IAssembly> & { assemblyComponents: Partial<IAssemblyComponent>[] }
  >();
  const removeVariationModal = useModal();
  const removeVariation = useDeleteVariation();
  const navigate = useNavigate();
  const { isOwnerOperator } = useUserRole();
  const [deletingStatus, setDeletingStatus] = useState(false);
  const assemblyConfig = watch('config') ?? getValues('parentAssembly.config');

  const handleUpdateRotationInterval = (
    value: number,
    type: keyof IAssemblyConfigLayout,
  ) => {
    const updatedAssemblyConfig = getValues('config') || {};
    const prevLayout = updatedAssemblyConfig.layout || {};

    const newConfig = {
      ...updatedAssemblyConfig,
      layout: {
        ...prevLayout,
        [type]: {
          ...(prevLayout[type] || {}),
          duration: value,
        },
      },
    };

    setValue(`config`, newConfig, { shouldDirty: true });
  };

  const sectionRotationDynamicSections = SectionRotationDynamicSections({
    sectionTypes: props.sectionTypes,
    setActiveAssemblyJourney: props.setActiveAssemblyJourney,
    activeAssemblyJourney: props.activeAssemblyJourney,
    onUpdateRotationInterval: handleUpdateRotationInterval,
    rotationIntervals: assemblyConfig?.layout,
    isOwnerOperator,
    disabled: !props.canEdit,
    setCustomerState: props.setCustomerState,
    customerState: props.customerState,
    setIsCodConfirmationOpen: props.setIsCodConfirmationOpen,
    onChangeDaypart: props.onChangeDaypart,
    selectedDaypartId: props.selectedDaypartId,
  }).map((section, index) => ({
    ...section,
    startIcon: <StartIcon>{index + 1}</StartIcon>,
  }));

  const editDrawerMenu = [
    {
      label: 'General details',
      content: (
        <InputContainer>
          <Controller
            control={control}
            name="name"
            rules={{
              validate: (value) =>
                !isEmpty(toString(value).trim()) ||
                'Assembly label is required',
            }}
            render={({ field }) => (
              <InputWidget
                {...field}
                value={field.value}
                label={lang.assemblyName}
                disabled={!props.canEdit}
                hasError={Boolean(formState.errors?.name?.message)}
                error={formState.errors?.name?.message}
              />
            )}
          />
          <TagLabel>{lang.assemblyTagText}</TagLabel>
          <Controller
            control={control}
            name="assembly_tag_id"
            render={({ field }) => (
              <FilterDropdownWithSearch
                name={lang.assemblyTagText}
                placeholder={lang.addAssemblyTag}
                multiline={true}
                onChange={(value: any) => {
                  field.onChange(value);
                }}
                value={field.value || []}
                badgeOptions={{
                  align: 'top',
                }}
              />
            )}
          />
        </InputContainer>
      ),
      disabledExpand: false,
      hasError: false,
      defaultExpanded: true,
    },
    ...sectionRotationDynamicSections,
  ];

  const handleRemoveVariation = async () => {
    setDeletingStatus(true);
    props.onDeleteAssemblyVariation();
    await removeVariation.mutateAsync(props.assemblyId);
    removeVariationModal.onCloseModal();
    Snackbar.success(`${getValues('name')} ${lang.successfullyDeleted}`);
    navigate({
      pathname: PATHS.ASSEMBLIES,
    });
  };

  return (
    <Container>
      <SidebarAccordion items={editDrawerMenu} />

      <Drawer open={props.openSectionDrawer}>
        <SectionDetailsSidebar
          selectedSection={props.selectedSection}
          onGoBack={props.onGoBack}
          onChangedAddComponent={props.onChangedAddComponent}
          onUpdateRotationDuration={props.onUpdateRotationDuration}
          onReplacedComponent={props.onReplacedComponent}
          onDeleteAssemblyComponent={props.onDeletedAssemblyComponent}
          onReorderRotation={props.onReorderRotation}
          handleComponentSelect={props.handleComponentSelect}
          onSelectBackupComponent={props.onSelectBackupComponent}
          parentAssemblyComponent={props.parentAssemblyComponent}
          childSectionEdit={props.childSectionEdit}
          handleChildEditSection={props.handleChildEditSection}
          onDeletedBackupAssemblyComponent={
            props.onDeletedBackupAssemblyComponent
          }
          parentBackupAssemblyComponents={props.parentBackupAssemblyComponents}
          isCodConfirmationOpen={props.isCodConfirmationOpen}
          activeAssemblyJourney={props.activeAssemblyJourney}
          customerState={props.customerState}
          assemblyId={props.assemblyId}
          handleUpdateBackupAssemblyComponents={
            props?.handleUpdateBackupAssemblyComponents
          }
          productFilters={props?.productFilters}
          productVariationFilters={props?.productVariationFilters}
          skipProductsApiOnEmptyFilters={props?.skipProductsApiOnEmptyFilters}
        />
      </Drawer>
      {getValues()?.variation_parent_id !== null &&
        getValues()?.variation_parent_id !== undefined &&
        getValues()?.status !== IStatus.READY &&
        props.disableRemoveVariation && (
          <RemoveVariationButtonContainer>
            <RemoveVariationButton
              onClick={removeVariationModal.onOpenModal}
              variant="text">
              Remove variation
            </RemoveVariationButton>
          </RemoveVariationButtonContainer>
        )}

      <SimpleDialog
        open={removeVariationModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.removeVariationModal.title}
        subTitle={lang.removeVariationModal.subTitle}
        onClose={removeVariationModal.onCloseModal}
        onClickCancel={removeVariationModal.onCloseModal}
        modalSize="sm"
        disabled={deletingStatus}
        onClickConfirm={handleRemoveVariation}
      />
    </Container>
  );
};
